import React, { useEffect, useState, useCallback } from 'react'
import styles from './index.module.scss'

interface BottomSheetProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
    isOpen,
    onClose,
    children,
    title,
}) => {
    const [visible, setVisible] = useState(false)
    const [closing, setClosing] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setClosing(false)
            setVisible(true)
        } else if (visible) {
            setClosing(true)
        }
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleAnimationEnd = useCallback(
        (e: React.AnimationEvent) => {
            // Only respond to the sheet's own animation, not children's
            if (e.target !== e.currentTarget) return
            if (closing) {
                setVisible(false)
                setClosing(false)
            }
        },
        [closing]
    )

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        },
        [onClose]
    )

    if (!visible) return null

    const overlayClass = `${styles.overlay} ${closing ? styles.overlayClosing : styles.overlayOpening}`
    const sheetClass = `${styles.sheet} ${closing ? styles.sheetClosing : styles.sheetOpening}`

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={overlayClass} onKeyDown={handleKeyDown}>
            <button
                type="button"
                className={styles.backdrop}
                onClick={onClose}
                aria-label="Close bottom sheet"
                tabIndex={-1}
            />

            <div
                className={sheetClass}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onAnimationEnd={handleAnimationEnd}
            >
                <div className={styles.handleBar} />
                {title && <h2 className={styles.title}>{title}</h2>}
                <div className={styles.content}>{children}</div>
            </div>
        </div>
    )
}

export default BottomSheet
